// Abas "Prazos" (atraso contra o primeiro baseline) e "Radar de faturamento"
// (entregue × estado da parcela — só leitura: quem fatura é o financeiro).
import { useEffect, useState } from 'react';
import { App as AntApp, Card, Col, Empty, Flex, Row, Segmented, Select, Spin, Statistic, Table, Tag, Typography } from 'antd';
import * as api from './api';
import type { Kanban, Prazos } from './api';
import { brl, dataBr, nomeDe, pct } from './regras';

const { Text } = Typography;

export function AbaPrazos() {
  const { message } = AntApp.useApp();
  const [ano, setAno] = useState(new Date().getFullYear());
  const [d, setD] = useState<Prazos | null>(null);
  const [filtro, setFiltro] = useState<'todos' | 'vencido' | 'entregue' | 'no_prazo'>('vencido');
  const [carregando, setCarregando] = useState(false);
  useEffect(() => {
    setCarregando(true);
    api.obterPrazos(ano).then(setD).catch(e => message.error(api.erroDe(e))).finally(() => setCarregando(false));
  }, [ano, message]);
  const ind = d?.indicadores;
  const linhas = (d?.linhas || []).filter(l => filtro === 'todos' || l.situacao === filtro)
    .sort((a, b) => b.atraso_dias - a.atraso_dias);
  return (
    <Spin spinning={carregando}>
      <Flex gap={12} wrap align="center" style={{ marginBottom: 12 }}>
        <Select style={{ width: 110 }} value={ano} onChange={setAno} showSearch optionFilterProp="label"
          options={[ano - 1, ano, ano + 1].map(a => ({ value: a, label: String(a) }))} />
        <Segmented value={filtro} onChange={v => setFiltro(v as any)}
          options={[{ value: 'vencido', label: 'Vencidos' }, { value: 'entregue', label: 'Entregues' }, { value: 'no_prazo', label: 'No prazo' }, { value: 'todos', label: 'Todos' }]} />
        <Text type="secondary">Prazo de referência: a data com que o entregável entrou no primeiro baseline.</Text>
      </Flex>
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title="Entregues no prazo" value={pct(ind?.pct_no_prazo)} /></Card></Col>
        <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title="Atraso médio (atrasados)" value={`${ind?.atraso_medio_dias ?? 0} dias`} /></Card></Col>
        <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title="Entregues no ano" value={ind?.entregues ?? 0} /></Card></Col>
        <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title="Vencidos sem entrega" value={ind?.vencidos ?? 0} valueStyle={{ color: ind?.vencidos ? '#d03b3b' : undefined }} /></Card></Col>
        <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title="Valor vencido" value={brl(ind?.valor_vencido)} /></Card></Col>
        <Col xs={12} md={8} xl={4}><Card size="small"><Statistic title="Replanejados" value={ind?.replanejados ?? 0} /></Card></Col>
      </Row>
      <Table rowKey="id" size="small" dataSource={linhas} pagination={{ pageSize: 50 }} scroll={{ x: 900 }}
        locale={{ emptyText: <Empty description="Nada aqui." /> }}
        columns={[
          { title: 'Projeto', render: (_, l) => <span><Text strong>{l.numero}</Text> {l.titulo}</span> },
          { title: 'Entregável', dataIndex: 'descricao' },
          { title: 'Responsável', dataIndex: 'responsavel', render: v => nomeDe(v) },
          { title: 'Valor', dataIndex: 'valor', align: 'right', render: v => brl(v) },
          { title: 'Prazo original', dataIndex: 'prevista', render: v => dataBr(v) },
          { title: 'Previsão atual', dataIndex: 'data_prevista', render: v => dataBr(v) },
          { title: 'Entregue em', dataIndex: 'entregue_em', render: v => dataBr(v) },
          {
            title: 'Atraso', dataIndex: 'atraso_dias', align: 'right', render: (v, l) => (
              <Tag color={l.situacao === 'vencido' ? 'red' : v > 0 ? 'orange' : 'green'}>{v > 0 ? `${v} dias` : v < 0 ? `${-v} dias antes` : 'no prazo'}</Tag>
            ),
          },
        ]} />
    </Spin>
  );
}

const COR_COLUNA: Record<string, string> = { sem_parcela: '#898781', a_liberar: '#eda100', liberada: '#2a78d6', faturada: '#1baf7a', recebida: '#0ca30c' };

export function AbaFaturamento() {
  const { message } = AntApp.useApp();
  const [d, setD] = useState<Kanban | null>(null);
  useEffect(() => { api.obterKanban().then(setD).catch(e => message.error(api.erroDe(e))); }, [message]);
  if (!d) return <Spin />;
  return (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        O que já foi entregue e onde está a parcela no financeiro. Só consulta: liberar BM, emitir NF e receber continuam no Contas a Receber.
        Recebidas aparecem por 60 dias.
      </Text>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        {d.colunas.map(c => (
          <Card key={c.chave} size="small" styles={{ header: { borderTop: `3px solid ${COR_COLUNA[c.chave]}` } }}
            title={<Flex justify="space-between"><span>{c.nome}</span><Tag>{c.cartoes.length}</Tag></Flex>}
            extra={null}>
            <Text type="secondary">{brl(c.total)}</Text>
            <Flex vertical gap={8} style={{ marginTop: 8, maxHeight: 'calc(100vh - 360px)', overflowY: 'auto' }}>
              {c.cartoes.length ? c.cartoes.map(k => (
                <Card key={k.id} size="small">
                  <Text strong>{k.numero}</Text> <Text type="secondary">{nomeDe(k.responsavel)}</Text>
                  <div>{k.descricao}</div>
                  <Flex justify="space-between" wrap>
                    <Text>{brl(k.valor, 2)}</Text>
                    <Text type="secondary">entregue {dataBr(k.entregue_em)}</Text>
                  </Flex>
                  {k.parcela_id && <Text type="secondary" style={{ fontSize: 12 }}>
                    Parcela {k.numero_parcela ?? '—'}{k.total_parcelas ? `/${k.total_parcelas}` : ''} · vence {dataBr(k.parcela_vencimento)}
                    {k.nota_fiscal_numero ? ` · NF ${k.nota_fiscal_numero}` : ''}
                  </Text>}
                </Card>
              )) : <Text type="secondary">—</Text>}
            </Flex>
          </Card>
        ))}
      </div>
    </div>
  );
}
